import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Patient, SessionPriceTier, BillingProfile } from '../../types/firestoreTypes';
import { Input, Dropdown, CountryDropdown } from '../ui';
import { Button } from '../ui/Button';
import { useToast } from '../../hooks/useToast';
import { useAnalytics } from '../../hooks/useAnalytics';

type PatientStatus = 'Active' | 'Closed' | 'Onboarding' | 'Prospect';

interface PatientDetailFormProps {
  patient: Patient;
  priceTiers: SessionPriceTier[];
  billingProfiles?: BillingProfile[];
  onSave: (patientId: string, data: Partial<Patient>) => Promise<void>;
  isSaving: boolean;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  status: PatientStatus;
  defaultPriceTierId: string;
  billingProfileId: string | null;
  address: {
    street: string;
    city: string;
    postalCode: string;
    country: string;
  };
}

export const PatientDetailForm: React.FC<PatientDetailFormProps> = ({
  patient,
  priceTiers,
  billingProfiles,
  onSave,
  isSaving,
}) => {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const { trackEvent } = useAnalytics();
  const [formData, setFormData] = useState<FormData>({
    name: patient.name || '',
    email: patient.email || '',
    phone: patient.phone || '',
    status: (patient.status as PatientStatus) || 'Active',
    defaultPriceTierId: patient.defaultPriceTierId || '',
    billingProfileId: patient.billingProfileId || null,
    address: {
      street: patient.address?.street || '',
      city: patient.address?.city || '',
      postalCode: patient.address?.postalCode || '',
      country: patient.address?.country || 'HU',
    },
  });

  useEffect(() => {
    setFormData({
      name: patient.name || '',
      email: patient.email || '',
      phone: patient.phone || '',
      status: (patient.status as PatientStatus) || 'Active',
      defaultPriceTierId: patient.defaultPriceTierId || '',
      billingProfileId: patient.billingProfileId || null,
      address: {
        street: patient.address?.street || '',
        city: patient.address?.city || '',
        postalCode: patient.address?.postalCode || '',
        country: patient.address?.country || 'HU',
      },
    });
  }, [patient]);

  const handleInputChange = (field: keyof Omit<FormData, 'address'> | `address.${keyof FormData['address']}`, value: string | null) => {
    setFormData((prev) => {
      const newData = structuredClone(prev);
      if (field.startsWith('address.')) {
        const addressField = field.split('.')[1] as keyof FormData['address'];
        (newData.address as any)[addressField] = value;
      } else {
        (newData as any)[field] = value;
      }
      return newData;
    });
  };

  const handleSaveChanges = async () => {
    const updateData: Partial<Patient> = {};

    if (formData.name !== patient.name) {
      updateData.name = formData.name;
    }
    if (formData.email !== patient.email) {
      updateData.email = formData.email;
    }
    if (formData.phone !== patient.phone) {
      updateData.phone = formData.phone;
    }
    if (formData.status !== patient.status) {
      updateData.status = formData.status;
    }
    if (formData.defaultPriceTierId !== patient.defaultPriceTierId) {
      updateData.defaultPriceTierId = formData.defaultPriceTierId;
    }
    if (formData.billingProfileId !== patient.billingProfileId) {
      updateData.billingProfileId = formData.billingProfileId;
    }

    const addressChanged =
      formData.address.street !== (patient.address?.street || '') ||
      formData.address.city !== (patient.address?.city || '') ||
      formData.address.postalCode !== (patient.address?.postalCode || '') ||
      formData.address.country !== (patient.address?.country || 'HU');

    if (addressChanged) {
      updateData.address = {
        street: formData.address.street,
        city: formData.address.city,
        postalCode: formData.address.postalCode,
        country: formData.address.country,
      };
    }

    if (Object.keys(updateData).length === 0) {
      // No changes to save
      return;
    }

    try {
      await onSave(patient.id, updateData);

      // Track the patient update
      trackEvent('patient_updated', {
        patient_id: patient.id,
        updated_fields: Object.keys(updateData),
        status: updateData.status || patient.status,
        has_address_changes: !!updateData.address,
        has_billing_changes: !!updateData.billingProfileId || !!updateData.defaultPriceTierId,
      });

      addToast({
        type: 'success',
        message: t('patientDetailForm.toasts.updateSuccess', { name: formData.name }),
      });
    } catch (error) {
      console.error('Error updating patient:', error);
      
      // Track the error
      trackEvent('error_occurred', {
        error_message: error instanceof Error ? error.message : 'Unknown error',
        context: 'patient_update',
        patient_id: patient.id,
      });

      addToast({
        type: 'error',
        message: t('patientDetailForm.toasts.updateFailed'),
      });
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-[#EAECF0] p-6 space-y-6">
      <h2 className="text-lg font-semibold text-gray-900">
        {t('patientDetailForm.heading')}
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-6">
          <Input
            label={t('patientDetailForm.form.name')}
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            required
          />

          <Input
            label={t('patientDetailForm.form.email')}
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
          />

          <Input
            label={t('patientDetailForm.form.phone')}
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
          />

          <Dropdown
            label={t('patientDetailForm.form.status')}
            options={[
              { value: 'Active', label: t('patients.status.Active') },
              { value: 'Closed', label: t('patients.status.Closed') },
              { value: 'Onboarding', label: t('patients.status.Onboarding') },
              { value: 'Prospect', label: t('patients.status.Prospect') },
            ]}
            value={formData.status}
            onChange={(value) => handleInputChange('status', value as PatientStatus)}
          />

          <Dropdown
            label={t('patientDetailForm.form.defaultPriceTier')}
            options={[
              { value: '', label: t('patientModal.form.selectPriceTier') },
              ...priceTiers.map((tier) => ({
                value: tier.id,
                label: tier.tierName,
              })),
            ]}
            value={formData.defaultPriceTierId}
            onChange={(value) => handleInputChange('defaultPriceTierId', value)}
          />

          <Dropdown
            label={t('patientDetailForm.form.billingProfile.label')}
            options={[
              { value: '', label: t('patientDetailForm.form.billingProfile.noneSelected') },
              ...(billingProfiles?.map(profile => ({
                value: profile.profileId,
                label: profile.profileName,
              })) || []),
            ]}
            value={formData.billingProfileId || ''}
            onChange={(value) => handleInputChange('billingProfileId', value || null)}
          />
        </div>

        <div className="space-y-6">
          <Input
            label={t('patientDetailForm.form.street')}
            value={formData.address.street}
            onChange={(e) => handleInputChange('address.street', e.target.value)}
          />

          <Input
            label={t('patientDetailForm.form.city')}
            value={formData.address.city}
            onChange={(e) => handleInputChange('address.city', e.target.value)}
          />

          <Input
            label={t('patientDetailForm.form.postalCode')}
            value={formData.address.postalCode}
            onChange={(e) => handleInputChange('address.postalCode', e.target.value)}
          />

          <CountryDropdown
            label={t('patientDetailForm.form.country')}
            value={formData.address.country || 'HU'}
            onChange={(code) => handleInputChange('address.country', code)}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleSaveChanges}
          isLoading={isSaving}
        >
          {t('patientDetailForm.buttons.save')}
        </Button>
      </div>
    </div>
  );
};
